/**
 * Admin invite management API endpoints (auth service).
 */

import { authApiClient } from '@/api/auth-api-client';

export interface InviteUser {
  id: string;
  name: string;
  email: string;
}

export interface AdminInvite {
  id: string;
  code: string;
  email: string | null;
  createdBy: string | null;
  usedBy: string | null;
  usedAt: string | null;
  expiresAt: string | null;
  createdAt: string;
  metadata: string | null;
  creator?: InviteUser;
  user?: InviteUser;
}

export interface ListInvitesResponse {
  invites: AdminInvite[];
  total: number;
}

export interface InviteStats {
  total: number;
  used: number;
  expired: number;
  active: number;
}

export interface ListInvitesParams {
  limit?: number;
  offset?: number;
  status?: 'active' | 'used' | 'expired' | 'all';
}

export interface CreateInviteParams {
  email?: string;
  expiresInDays?: number;
}

export async function listInvites(params: ListInvitesParams = {}): Promise<ListInvitesResponse> {
  const query = new URLSearchParams();
  if (params.limit !== undefined) query.set('limit', String(params.limit));
  if (params.offset !== undefined) query.set('offset', String(params.offset));
  if (params.status) query.set('status', params.status);
  const qs = query.toString();
  return authApiClient.get<ListInvitesResponse>(`/api/admin/invites${qs ? `?${qs}` : ''}`);
}

export async function getInviteStats(): Promise<InviteStats> {
  return authApiClient.get<InviteStats>('/api/admin/invites/stats');
}

export async function createInvite(data: CreateInviteParams): Promise<AdminInvite> {
  return authApiClient.post<AdminInvite>('/api/admin/invites', data);
}

export async function sendInviteEmail(code: string): Promise<{ success: boolean }> {
  return authApiClient.post<{ success: boolean }>(`/api/admin/invites/${code}/send-email`);
}

export interface UpdateInviteEmailParams {
  email: string | null;
}

export async function updateInviteEmail(code: string, data: UpdateInviteEmailParams): Promise<AdminInvite> {
  return authApiClient.patch<AdminInvite>(`/api/admin/invites/${code}`, data);
}

export async function deleteInvite(code: string): Promise<{ success: boolean }> {
  return authApiClient.delete<{ success: boolean }>(`/api/admin/invites/${code}`);
}
